import { SHIFT_CONFIG_COLORS } from "@/shared/components/KPICard";
import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { RollingTimeData } from "../models/ShiftReportResponse";

const moment = getMomentTz();

type Props = {
	data: RollingTimeData | null;
};

export const RollingTimeCard = ({ data }: Props) => {
	const timeLeftSecs = data?.TimeLeftTillRollChangeSecs ?? null;

	if (!data) return null;

	const targetedSecs = data.TargetedRollingTimeSecs ?? 0;

	const timeLeftPct =
		timeLeftSecs != null && targetedSecs > 0
			? (timeLeftSecs / targetedSecs) * 100
			: timeLeftSecs != null && timeLeftSecs > 0
				? 100
				: 0;
	const cappedPct = Math.min(Math.max(timeLeftPct, 0), 100);

	const details = [
		{
			label: "Expected Roll Out",
			value: data.ExpectedRollOutTime
				? dxtToLocalServerTime(data.ExpectedRollOutTime, "MM/dd HH:mm")
				: "-",
			tooltip: "Projected time when roll change will occur",
		},
		{
			label: "Targeted Duration",
			value: moment
				.duration(data.TargetedRollingTimeSecs, "seconds")
				.format("h[h] m[m]"),
			tooltip: "Expected total rolling duration",
		},
		{
			label: "Scheduled Roll Change",
			value: data.ScheduledRollChangeHours
				? moment
						.duration(data.ScheduledRollChangeHours, "hours")
						.format("h[h] m[m]")
				: "-",
			tooltip: "Scheduled roll change time for the next section",
		},
		// Changeover Time hidden for now (semantics pending rework — see C4 in release review).
		// {
		// 	label: "Changeover Time",
		// 	value: data.ChangeoverTimeSecs ? moment
		// 		.duration(data.ChangeoverTimeSecs, "seconds")
		// 		.format("h[h] m[m]") : '-',
		// 	tooltip: "Time between previous and current rolling run",
		// },
	];

	const circleSize = 140;
	const strokeWidth = 8;

	return (
		<Paper
			elevation={0}
			sx={{
				p: 1,
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				border: "1px solid #ddd",
				borderRadius: 1,
				backgroundColor: "#fff",
				userSelect: "none",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
				}}
			>
				<Typography
					sx={{
						fontWeight: 600,
						color: "#333",
						fontSize: "0.85rem",
						lineHeight: 1,
						whiteSpace: "nowrap",
					}}
				>
					Next Roll Change
				</Typography>
			</Box>

			<Box
				sx={{
					height: 150,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Tooltip title="Time remaining until roll change" placement="top">
					<Box sx={{ position: "relative", display: "inline-flex" }}>
						<CircularProgress
							variant="determinate"
							value={100}
							size={circleSize}
							thickness={strokeWidth / 2}
							sx={{
								color: "#e0e0e0",
								position: "absolute",
							}}
						/>
						<CircularProgress
							variant="determinate"
							value={cappedPct}
							size={circleSize}
							thickness={strokeWidth / 2}
							sx={{
								color: SHIFT_CONFIG_COLORS[data.TimeLeftColor],
								transform: "rotate(-90deg) !important",
								"& .MuiCircularProgress-circle": {
									strokeLinecap: "round",
									transition: "stroke-dashoffset 1s linear",
								},
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								bottom: 0,
								right: 0,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Typography
								sx={{
									fontSize: "1.2rem",
									fontWeight: 700,
									color: SHIFT_CONFIG_COLORS[data.TimeLeftColor],
									lineHeight: 1,
								}}
							>
								{timeLeftSecs != null
									? moment.duration(timeLeftSecs, "seconds").format("h[h] m[m]")
									: "-"}
							</Typography>
							<Typography
								sx={{
									fontSize: "0.65rem",
									color: "#666",
									mt: 0.5,
								}}
							>
								Time Left
							</Typography>
							{(data.CurrentSectionName || data.NextSectionName) && (
								<Typography
									sx={{
										fontSize: "0.6rem",
										color: "#666",
										mt: 0.5,
										whiteSpace: "nowrap",
									}}
								>
									{data.CurrentSectionName || "—"} →{" "}
									{data.NextSectionName || "—"}
								</Typography>
							)}
						</Box>
					</Box>
				</Tooltip>
			</Box>

			<Box
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-end",
				}}
			>
				<Box sx={{ pt: 0.5, borderTop: "1px solid #ddd" }}>
					{details.map((detail, index) => (
						<Tooltip
							key={index}
							title={detail.tooltip}
							arrow
							placement="bottom"
						>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									minHeight: 16,
									userSelect: "none",
								}}
							>
								<Typography sx={{ fontSize: "0.6rem", color: "#333" }}>
									{detail.label}
								</Typography>
								<Typography
									sx={{
										fontSize: "0.6rem",
										fontWeight: 600,
										color: "#333",
									}}
								>
									{detail.value}
								</Typography>
							</Box>
						</Tooltip>
					))}
				</Box>
			</Box>
		</Paper>
	);
};
